"use client"

import { useState, useEffect } from "react"
import { Card, InputNumber, Button, Typography, Spin, message } from "antd"
import { useDispatch } from "react-redux"
import { request } from '../../../shared/src/apis';
import { TruckingModal } from '@operations/shared';
import { getCommodityDataApi, updateCommodityPriceApi } from "../utils/apisConsts"

const { Title, Text } = Typography

interface ICommodityData {
  commodityType: string | null
  pricePerOunce: number | null
}

interface IApiResponse {
  status: number
  message: string
  data: {
    commodityType: string
    commodityPricePerOunce: number
  }
}

export function CommodityPrice() {
  const dispatch = useDispatch()
  const [commodityData, setCommodityData] = useState<ICommodityData>({
    commodityType: null,
    pricePerOunce: null,
  })
  const [loading, setLoading] = useState<boolean>(true)
  const [updating, setUpdating] = useState<boolean>(false)
  const [newPrice, setNewPrice] = useState<number | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [visibleModal, setVisibleModal] = useState<boolean>(false)

  useEffect(() => {
    fetchCommodityData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  const fetchCommodityData = async () => {
    try {
      setLoading(true)
      const response: any = await dispatch(request({
        method: 'GET',
        url: getCommodityDataApi
      }))
      const result: IApiResponse = response?.data
      if (result?.status === 200 && result?.data) {
        setCommodityData({
          commodityType: result?.data?.commodityType ?? null,
          pricePerOunce: result?.data?.commodityPricePerOunce ?? null,
        })
      } else {
        message.error(result?.message ?? "Failed to fetch commodity data")
      }
    } catch (error) {
      console.error("Error fetching commodity data:", error)
      message.error("Something went wrong, please try again")
    } finally {
      setLoading(false)
    }
  }
  // const formatPrice = (price: number | null) => {
  //   if (price === null || Number.isNaN(price)) {
  //     return "N/A"
  //   }
  //   return price.toLocaleString("en-US", {
  //     minimumFractionDigits: 20,
  //     maximumFractionDigits: 20,
  //   })
  // }

  const handleUpdatePrice = () => {
    if (newPrice === null || newPrice === undefined || newPrice <= 0) {
      setValidationError("Please enter a valid price greater than 0")
      return
    }
    setValidationError(null)
    setVisibleModal(true)
  }

  const handleConfirmUpdate = async () => {
    if (newPrice === null || newPrice === undefined || newPrice <= 0) {
      setValidationError("Please enter a valid price greater than 0")
      return
    }

    try {
      setUpdating(true)
      const response: any = await dispatch(request({
        method: 'PATCH',
        url: updateCommodityPriceApi,
        data: {
          commodityPricePerOunce: newPrice
        }
      }))

      const result: IApiResponse = response?.data

      if (result?.status === 200) {
        message.success(result?.message ?? "Price updated successfully")
        setVisibleModal(false)
        setNewPrice(null)
        // Refresh commodity data
        await fetchCommodityData()
      } else {
        message.error(result?.message ?? "Failed to update commodity price")
      }
    } catch (error) {
      console.error("Error updating commodity price:", error)
      message.error("Something went wrong, please try again")
    } finally {
      setUpdating(false)
    }
  }

  const handleCloseModal = () => {
    setVisibleModal(false)
  }

  const handlePriceChange = (value: number | null) => {
    setNewPrice(value)
    // Real-time validation: show error if 0 or less, hide if greater than 0
    if (value === null || value === undefined) {
      setValidationError(null)
    } else if (value <= 0) {
      setValidationError("Please enter a valid price greater than 0")
    } else {
      setValidationError(null)
    }
  }

  const priceDifference = commodityData?.pricePerOunce && newPrice
    ? newPrice - commodityData.pricePerOunce
    : null
  const priceChangePercent = commodityData?.pricePerOunce && priceDifference
    ? ((priceDifference / commodityData.pricePerOunce) * 100)
    : null

  if (loading) {
    return (
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px" }}>
      <Card className="commodity-card-creative" bodyStyle={{ padding: "32px" }}>
        {/* Header */}
        <div className="action-header">
          <Title level={2} style={{ margin: 0, color: "white", fontWeight: 700, fontSize: 24 }}>
            Commodity Price Management
          </Title>
          <Text style={{ fontSize: 14, color: "rgba(255,255,255,0.9)", display: "block", marginTop: 8 }}>
            Manage and update commodity prices in real-time
          </Text>
        </div>

        {/* Info Section */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 20,
            marginBottom: 32,
          }}
        >
          {/* Commodity Type */}
          <div className="info-card-platinum" style={{ position: "relative", zIndex: 1 }}>
            <Text style={{ fontSize: 12, opacity: 0.9, display: "block", marginBottom: 10, fontWeight: 600, letterSpacing: "1px" }}>
              COMMODITY TYPE
            </Text>
            <Title level={1} style={{ margin: 0, fontSize: 36, fontWeight: 800, color: "white" }}>
              {commodityData?.commodityType ?? "N/A"}
            </Title>
          </div>

          {/* Price per Ounce */}
          <div className="info-card-blue" style={{ position: "relative", zIndex: 1 }}>
            <Text style={{ fontSize: 12, opacity: 0.9, display: "block", marginBottom: 10, fontWeight: 600, letterSpacing: "1px", color: "white" }}>
              UNIT PRICE
            </Text>
            <Title level={1} style={{ margin: 0, fontSize: 36, fontWeight: 800, color: "white", fontFamily: "monospace" }}>
              SAR {(commodityData?.pricePerOunce ?? null)}
            </Title>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: "2px solid #f0f0f0", marginBottom: 32 }} />

        {/* Update Price Section */}
        <div>
          <Title level={3} style={{ marginBottom: 20, fontWeight: 700, fontSize: 22, color: "#262626" }}>
            Update Price
          </Title>

          <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <Text style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 10, color: "#262626" }}>
                New Price (SAR)
              </Text>
              <div className={validationError ? "creative-input-wrapper creative-input-wrapper-error" : "creative-input-wrapper"}>
                <InputNumber
                  prefix="SAR "
                  placeholder="0.00"
                  value={newPrice === null ? undefined : newPrice}
                  onChange={(value) => handlePriceChange(typeof value === "number" ? value : null)}
                  style={{
                    width: "100%",
                  }}
                  controls={false}
                />
              </div>
              <div className="input-error-message">
                {validationError && (
                  <Text type="danger" style={{ fontSize: 13, display: "block", fontWeight: 500 }}>
                    ⚠️ {validationError}
                  </Text>
                )}
              </div>
            </div>
            <div style={{ marginTop: "34px" }}>
              <Button
                type="primary"
                size="large"
                onClick={handleUpdatePrice}
                className="creative-update-btn"
              >
                ✨ Update Price
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Confirmation Modal */}
      <TruckingModal
        visibleModal={visibleModal}
        handleCloseModal={handleCloseModal}
        handleAcceptModal={handleConfirmUpdate}
        loadingConfirm={updating}
        title="Confirm Price Update"
        width={650}
        content={
          <div style={{ padding: "8px 0" }}>
            <Text type="secondary" style={{ fontSize: 14, display: "block", marginBottom: 20 }}>
              You are about to update the price for <strong>{commodityData?.commodityType ?? "the commodity"}</strong>
            </Text>

            {/* Current Price */}
            <div style={{ marginBottom: 16, padding: 18, background: "#f8f9fa", borderRadius: "12px", border: "1px solid #e8e8e8" }}>
              <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 8, fontWeight: 600 }}>
                Current Price
              </Text>
              <Title level={2} style={{ margin: 0, fontFamily: "monospace", fontWeight: 700, color: "#262626", fontSize: 28 }}>
                SAR {(commodityData?.pricePerOunce ?? null)}
              </Title>
            </div>

            {/* New Price Display */}
            <div className="modal-price-box" style={{ position: "relative", zIndex: 1 }}>
              <Text style={{ fontSize: 13, opacity: 0.9, display: "block", marginBottom: 12, fontWeight: 600, letterSpacing: "1px", color: "white" }}>
                NEW PRICE
              </Text>
              <Title level={1} style={{ margin: 0, fontFamily: "monospace", fontWeight: 800, color: "white", fontSize: 42 }}>
                SAR {(newPrice ?? null)}
              </Title>
              {priceDifference !== null && priceChangePercent && (
                <div style={{ marginTop: 16, fontSize: 15, fontWeight: 600 }}>
                  {priceDifference > 0 ? (
                    <span style={{ color: "#4ade80" }}>↑ +SAR {(priceDifference)} ({priceChangePercent}%)</span>
                  ) : (
                    <span style={{ color: "#fca5a5" }}>↓ SAR {(priceDifference)} ({priceChangePercent}%)</span>
                  )}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
              <Button
                onClick={handleCloseModal}
                className="modal-btn-creative modal-btn-cancel-creative"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmUpdate}
                className="modal-btn-creative modal-btn-confirm-creative"
              >
                Confirm Update
              </Button>
            </div>
          </div>
        }
      />
    </div>
  )
}
